import { Container, Loading } from "@fluidattacks/design";
import * as React from "react";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { SectionHeader } from "components/section-header";
import { authContext } from "context/auth";
import { usePermissions } from "context/authz/hooks/use-permissions";

const ToDoTemplate: React.FC<Readonly<{ children: JSX.Element }>> = ({
  children,
}): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const url = "/todos";

  const { loading, someGroupPermissions } = usePermissions();
  const { userEmail } = useContext(authContext);

  useEffect((): void => {
    void someGroupPermissions.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Container
        alignItems={"center"}
        display={"flex"}
        height={"100%"}
        justify={"center"}
        width={"100%"}
      >
        <Loading />
      </Container>
    );
  }

  const canRenderReattacks = userEmail.endsWith("@fluidattacks.com");
  const canRenderLocation = someGroupPermissions.canResolve(
    "group_vulnerability_drafts",
  );

  const canRenderDrafts =
    someGroupPermissions.canMutate("approve_evidence") ||
    someGroupPermissions.canMutate("reject_evidence");

  const canRenderSeverity =
    someGroupPermissions.canMutate("approve_vulnerabilities_severity_update") ||
    someGroupPermissions.canMutate("reject_vulnerabilities_severity_update");

  return (
    <Container bgColor={theme.palette.gray[50]} height={"100%"}>
      <SectionHeader
        header={"Your to-do list"}
        tabs={[
          {
            id: "assignedLocations",
            label: t("todoList.tabs.assignedLocations"),
            link: `${url}/assigned-locations`,
            tooltip: t("todoList.tooltip.assignedLocations"),
          },
          {
            id: "fixPriority",
            label: t("todoList.tabs.fixPriority.title"),
            link: `${url}/fix-priority`,
            tooltip: t("todoList.tooltip.fixPriority"),
          },
          ...(canRenderReattacks
            ? [
                {
                  id: "Reattacks",
                  label: t("todoList.tabs.reattacks"),
                  link: `${url}/reattacks`,
                  tooltip: t("todoList.tooltip.reattacks"),
                },
              ]
            : []),
          ...(canRenderLocation
            ? [
                {
                  id: "locationDrafts",
                  label: t("todoList.tabs.locationDrafts"),
                  link: `${url}/location-drafts`,
                  tooltip: t("todoList.tooltip.locationDrafts"),
                },
              ]
            : []),
          ...(canRenderSeverity
            ? [
                {
                  id: "vulnSeverityRequests",
                  label: t("todoList.tabs.vulnSeverityRequests"),
                  link: `${url}/vuln-severity-requests`,
                  tooltip: t("todoList.tooltip.vulnSeverityRequests"),
                },
              ]
            : []),
          ...(canRenderDrafts
            ? [
                {
                  id: "evidenceDrafts",
                  label: t("todoList.tabs.evidenceDrafts.title"),
                  link: `${url}/evidence-drafts`,
                  tooltip: t("todoList.tooltip.evidenceDrafts"),
                },
              ]
            : []),
          {
            id: "tasksEvents",
            label: t("Events"),
            link: `${url}/events`,
            tooltip: t("todoList.tooltip.events"),
          },
        ]}
      />
      <Container px={1.25} py={1.25}>
        {children}
      </Container>
    </Container>
  );
};

export { ToDoTemplate };
